import { isEmpty } from "lodash";

import {
    getResults,
    organizeDataForDisplay,
    getSavingsTableData,
    getWaterfallData,
    isHidden,
} from "utils/communities";
import {
    COMMUNITY_GRAPH_TOP_LABELS,
    COMMUNITY_GRAPH_LEGEND_LABELS,
    COMMUNITY_TABLE_HEADERS,
} from "utils/communities/constants";

/** Plain ASCII for Excel CSV (avoids m³/tCO₂/— mojibake when opened as Latin-1). */
const toCsvSafeText = (value) => {
    if (value == null) {
        return "";
    }
    return String(value)
        .replace(/\u00B3/g, "3")
        .replace(/\u00B2/g, "2")
        .replace(/\u2080/g, "0")
        .replace(/\u2081/g, "1")
        .replace(/\u2082/g, "2")
        .replace(/\u2083/g, "3")
        .replace(/\u2084/g, "4")
        .replace(/[\u2013\u2014]/g, "-")
        .replace(/\u00D7/g, "x");
};

const escapeCsvCell = (value) => {
    const cell = toCsvSafeText(value);
    if (/[",\n\r]/.test(cell)) {
        return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
};

const formatCsvRows = (rows) => rows.map((row) => row.map(escapeCsvCell));

const blankRow = () => ["", ""];

const pushSectionTitle = (rows, title) => {
    rows.push(blankRow());
    rows.push([title, ""]);
};

const pushWideMetricTable = (rows, legendItems, displayData) => {
    if (isEmpty(displayData)) {
        rows.push(["(no data)", ""]);
        return;
    }

    rows.push(["Metric", ...displayData.map((item) => item.packageName)]);

    legendItems.forEach((legendItem) => {
        if (legendItem.isSeparator) {
            return;
        }

        const { label, dataKey } = legendItem;
        rows.push([
            label,
            ...displayData.map((item) => {
                const value = item[dataKey];
                return value == null || value === "" ? "" : value;
            }),
        ]);
    });
};

const getLegendItemsForMetric = (metricKey) => [
    ...(COMMUNITY_GRAPH_LEGEND_LABELS[metricKey]?.top || []),
    { isSeparator: true },
    ...(COMMUNITY_GRAPH_LEGEND_LABELS[metricKey]?.bottom || []),
];

const getSortedUpgradePackageIds = (allPackages = {}) =>
    Object.entries(allPackages)
        .filter(([packageId, pkg]) => packageId !== "base" && !pkg?.archived)
        .sort(([, a], [, b]) => (a?.ui?.displayOrder ?? 0) - (b?.ui?.displayOrder ?? 0))
        .map(([packageId]) => packageId);

const formatNumeric = (value, decimals = 2) => {
    const n = typeof value === "number" ? value : parseFloat(value);
    if (!Number.isFinite(n)) {
        return "";
    }
    return n.toFixed(decimals);
};

const pushSavingsByMeasureSection = (rows, tableData) => {
    rows.push([
        "Measure Category",
        "Electricity Savings (kWh)",
        "Natural Gas Savings (m3)",
        "Propane Savings (L)",
        "Oil Savings (L)",
        "Total Energy Savings without PV (GJ)",
        "Total Energy Savings with PV (GJ)",
        "Total Emissions Savings (tCO2)",
    ]);

    tableData.forEach((row) => {
        if (isHidden(row, "table")) {
            return;
        }

        rows.push([
            COMMUNITY_TABLE_HEADERS[row.upgradeType] || row.upgradeType,
            formatNumeric(row.elecSavingsKWH),
            formatNumeric(row.gasSavingsM3),
            formatNumeric(row.propaneSavingsL),
            formatNumeric(row.oilSavingsL),
            formatNumeric(row.aecSavingsGJ),
            formatNumeric(row.netSavingsGJ),
            formatNumeric(row.emissionsSavingsTCO2),
        ]);
    });
};

const pushWaterfallSection = (rows, waterfallData, metricLabel) => {
    rows.push([
        "Measure Category",
        `${metricLabel} - Average % Savings`,
        "Start Energy (%)",
        "End Energy (%)",
        "Start Emissions (%)",
        "End Emissions (%)",
    ]);

    waterfallData.forEach((row) => {
        if (isHidden(row, "waterfall", metricLabel === "Energy")) {
            return;
        }

        const savingsKey = metricLabel === "Energy" ? "averagePercentEnergySaving" : "averagePercentEmissionSaving";

        rows.push([
            COMMUNITY_TABLE_HEADERS[row.upgradeType] || row.upgradeType,
            row[savingsKey] == null ? "" : formatNumeric(row[savingsKey], 2),
            formatNumeric(row.startEnergy, 2),
            formatNumeric(row.endEnergy, 2),
            formatNumeric(row.startEmissions, 2),
            formatNumeric(row.endEmissions, 2),
        ]);
    });
};

/**
 * Excel-friendly CSV for community results charts and tables (excludes aggregated baseline data).
 * Wide tables: metrics as rows, upgrade packages as columns; savings/waterfall per package as sections.
 */
export const buildCommunityResultsChartCsv = (
    runData,
    selectedModelIds,
    {
        allPackages = {},
        communityModelCountsMap = {},
        fuelPrices = {},
        applyModelResultMultiplier = false,
    } = {}
) => {
    const rows = [["Parameter", "Value"]];

    const results = getResults(selectedModelIds, runData, fuelPrices, communityModelCountsMap);
    const displayData = organizeDataForDisplay(results, allPackages, applyModelResultMultiplier);

    ["energy", "opEmissions", "opCost"].forEach((metricKey) => {
        pushSectionTitle(rows, COMMUNITY_GRAPH_TOP_LABELS[metricKey] || metricKey);
        pushWideMetricTable(rows, getLegendItemsForMetric(metricKey), displayData);
    });

    const runResultsList = Object.values(runData?.runResults || {});
    const upgradePackageIds = getSortedUpgradePackageIds(allPackages);

    upgradePackageIds.forEach((packageId) => {
        const packageName = allPackages[packageId]?.name || packageId;

        pushSectionTitle(rows, `Savings by Measure - ${packageName}`);

        const tableData = getSavingsTableData(
            runData?.runResultsByMeasure,
            packageId,
            selectedModelIds,
            communityModelCountsMap,
            applyModelResultMultiplier
        );
        pushSavingsByMeasureSection(rows, tableData);

        pushSectionTitle(rows, `Waterfall - Energy Savings (%) - ${packageName}`);
        const { waterfall: energyWaterfall = [] } = getWaterfallData(
            runResultsList,
            runData?.runResultsByMeasure,
            packageId,
            selectedModelIds,
            communityModelCountsMap,
            applyModelResultMultiplier
        );
        pushWaterfallSection(rows, energyWaterfall, "Energy");

        pushSectionTitle(rows, `Waterfall - Emissions Savings (%) - ${packageName}`);
        pushWaterfallSection(rows, energyWaterfall, "Emissions");
    });

    rows.push(blankRow());
    rows.push(["Include Duplicates", applyModelResultMultiplier ? "Yes" : "No"]);

    return formatCsvRows(rows);
};
